import { z } from 'zod';
import { provenanceSourceSchema } from './provenance.js';
const uuid = z.string().uuid();
/**
 * Кости и журнал бросков (D12).
 *
 * Бросок — команда серверу, а не вычисление на клиенте (D3): за столом
 * бросок клиента ничем не отличается от набранного числа, и доверия
 * к нему столько же. Поэтому случайность живёт на сервере, а сюда
 * приходит результат — вместе с тем, что выпало на каждой кости.
 *
 * Ручной ввод равноправен (D12): игрок бросил настоящие кости и внёс
 * число. Оно попадает в тот же журнал и помечается — не чтобы ловить
 * жуликов, а чтобы за столом не было спора, чей бросок настоящий.
 *
 * Движок кости не бросает (D21.4). Разбор записи, отбор оставленных
 * костей и сумма — правила и живут в `packages/rules`; случайность —
 * работа сервера, и в правилах её нет.
 */
/** Кости из таблицы источника: других в SRD 5.2 не встречается. */
export const DIE_SIDES = [4, 6, 8, 10, 12, 20, 100];
export const dieSidesSchema = z.union([
    z.literal(4),
    z.literal(6),
    z.literal(8),
    z.literal(10),
    z.literal(12),
    z.literal(20),
    z.literal(100),
]);
/**
 * Откуда взялась прибавка. Те же источники, что у величин листа
 * (D5), плюс запись броска: «+3» из `2d6+3` не приходит ниоткуда,
 * его набрал человек, и в журнале это должно быть видно.
 */
export const rollModifierSourceSchema = z.union([
    provenanceSourceSchema,
    z.strictObject({ kind: z.literal('notation') }),
]);
export const rollModifierSchema = z.strictObject({
    source: rollModifierSourceSchema,
    value: z.number().int(),
});
/**
 * Что кидаем.
 *
 * Преимущество и помеха — не отдельный вид броска, а `2d20`
 * с оставленной лучшей или худшей костью; бросок характеристики —
 * `4d6`, оставить три лучшие. Одна модель на три случая: отдельные
 * поля «преимущество» и «отбросить худшую» пришлось бы складывать
 * друг с другом, а сложить их нечем.
 */
export const diceRequestSchema = z.strictObject({
    count: z.number().int().positive().max(100),
    sides: dieSidesSchema,
    /** Оставить лучшие или худшие; `null` — считать все. */
    keep: z
        .strictObject({
        kind: z.enum(['highest', 'lowest']),
        count: z.number().int().positive(),
        /**
         * Признак листа, из-за которого кости две: «преимущество
         * на спасброски Интеллекта». `null` — преимущество или помеху
         * выбрал человек, а не правило. В журнале это разные вещи:
         * первое видно объяснением, второе — его решением.
         */
        because: z.string().min(1).nullable(),
    })
        .nullable(),
    modifiers: z.array(rollModifierSchema),
});
export const rollDieSchema = z.strictObject({
    value: z.number().int().positive(),
    /** Попала в сумму. Отброшенные кости остаются видны: их бросали. */
    kept: z.boolean(),
});
export const rollOutcomeSchema = z.strictObject({
    request: diceRequestSchema,
    /**
     * Что выпало, в порядке броска. У ручного ввода список пуст: кости
     * бросали настоящие, и приложение их не видело.
     */
    dice: z.array(rollDieSchema),
    /** Сумма оставленных костей — до прибавок. */
    diceTotal: z.number().int(),
    total: z.number().int(),
    /** Число внесено человеком (D12). */
    manual: z.boolean(),
});
/**
 * Зачем кидали. Ключ переводится интерфейсом, `subject` — то, о чём
 * бросок: запись оружия, ключ навыки, имя характеристики.
 */
export const rollReasonSchema = z.strictObject({
    kind: z.enum([
        'attack',
        'damage',
        'check',
        'save',
        'initiative',
        'ability_pool',
        /** Кость хитов в короткий отдых (с. 187). */
        'hit_die',
        /** Кость хитов при повышении уровня (с. 23). */
        'level_hp',
        'custom',
    ]),
    subject: z.string().nullable(),
});
export const rollEntrySchema = z.strictObject({
    id: uuid,
    characterId: uuid,
    /**
     * Кампания, в которой бросали. Пока журнал личный (партии нет),
     * но место под общий оставлено здесь, а не в будущей миграции:
     * бросок без кампании и бросок за столом — одна и та же запись.
     */
    campaignId: uuid.nullable(),
    reason: rollReasonSchema,
    outcome: rollOutcomeSchema,
    at: z.string(),
});
//# sourceMappingURL=dice.js.map